import { ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { combineLatest, mergeMap, Observable, Subscription, switchMap } from "rxjs";
import { filter, map, shareReplay, startWith, withLatestFrom } from "rxjs/operators";
import { AsyncPipe, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from "@angular/common";
import { GetProductDetailQuery, ProductPreviewFragment, SortOrder } from "../../common/gql/graphql";
import { HasMetadata } from "../../../common/types";
import { getMostRelevantCollection } from "../../../common/utils/get-most-relevant-collection";
import { notNullOrUndefined } from "../../../common/utils/not-null-or-undefined";
import { NotificationService } from "../../../core/providers/notification/notification.service";
import { StateService } from "../../../core/providers/state.service";
import { JsonLd, JsonLdService } from "../../providers/json-ld.service";
import {
  BreadcrumbDef,
  CollectionBreadcrumbsComponent
} from "../collection-breadcrumbs/collection-breadcrumbs.component";
import { OrderService } from "../../../core/providers/order/order.service";
import { DataService } from "../../../core/providers/data/data.service";
import { TopReviewsComponent } from "../top-reviews/top-reviews.component";
import { BlogFeedComponent } from "../../../shared/components/blog-feed/blog-feed.component";
import { ProductRecommendationsComponent } from "../product-recommendations/product-recommendations.component";
import { JsonLdComponent } from "../../../shared/components/json-ld/json-ld.component";
import { ProductPageDefaultComponent } from "../product-page-default/product-page-default.component";
import { ProductPageSelectMenuComponent } from "../product-page-select-menu/product-page-select-menu.component";
import { ProductPageGiftCardComponent } from "../product-page-gift-card/product-page-gift-card.component";
import { ProductPageColorChartComponent } from "../product-page-color-chart/product-page-color-chart.component";
import { DotdigitalService } from "../../../core/providers/dotdigital/dotdigital.service";
import { GET_PRODUCT_DETAIL } from "./product-detail.graphql";

type Product = NonNullable<GetProductDetailQuery['product']>;

@Component({
    selector: 'vsf-product-detail',
    templateUrl: './product-detail.component.html',
    styleUrls: ['./product-detail.component.scss'],
    standalone: true,
    imports: [
        CollectionBreadcrumbsComponent,
        NgSwitch,
        NgSwitchCase,
        ProductPageColorChartComponent,
        ProductPageGiftCardComponent,
        ProductPageSelectMenuComponent,
        NgSwitchDefault,
        ProductPageDefaultComponent,
        JsonLdComponent,
        ProductRecommendationsComponent,
        BlogFeedComponent,
        NgIf,
        TopReviewsComponent,
        RouterLink,
        AsyncPipe,
    ],
})
export class ProductDetailComponent implements OnInit, OnDestroy, HasMetadata {
    readonly productData$: Observable<NonNullable<GetProductDetailQuery['product']>> =
        this.route.paramMap.pipe(
            switchMap((pm) =>
                this.dataService.query(
                    GET_PRODUCT_DETAIL,
                    {
                        slug: pm.get('slug') || '',
                        reviewOptions: {
                            take: 3,
                        },
                    },
                    { ssr: true },
                ),
            ),
            map((data) => data.product),
            filter(notNullOrUndefined),
            shareReplay(1),
        );
    readonly product$ = this.productData$.pipe(
        filter(notNullOrUndefined),
    );
    productPreview$: Observable<ProductPreviewFragment>;
    pageType$: Observable<NonNullable<ProductPreviewFragment['customFields']>['pageType']>;
    product: Product | undefined;
    selectedAssetId: string;
    breadcrumbs: BreadcrumbDef[] | null = null;
    jsonLdData$: Observable<JsonLd>;
    relatedBlogPostIds: string[] = [];
    @ViewChild('addedToCartTemplate', { static: true })
    private addToCartTemplate: TemplateRef<any>;
    @ViewChild('addedToWishlistTemplate', { static: true })
    private addToWishlistTemplate: TemplateRef<any>;
    private sub: Subscription;

    constructor(
        private dataService: DataService,
        private stateService: StateService,
        private notificationService: NotificationService,
        private route: ActivatedRoute,
        private changeDetector: ChangeDetectorRef,
        private jsonLdService: JsonLdService,
        private orderService: OrderService,
        private dotdigitalService: DotdigitalService,
    ) {
        this.productPreview$ = this.route.data.pipe(map((data) => data['entity']));
        this.pageType$ = this.productPreview$.pipe(
            map(({ customFields }) => customFields?.pageType ?? 'default'),
            startWith(this.route.snapshot.data['entity'].customFields?.pageType ?? 'default'),
        );
    }

    pageMetadata = this.productData$.pipe(
        mergeMap((product) => this.productPreview$.pipe(map((preview) => [product, preview] as const))),
        map(([product, preview]) => {
            const imagePreview = product.customFields?.seoImage?.preview ?? product.featuredAsset?.preview;
            const description =
                product.customFields?.seoDescription ??
                `Buy ${preview.name} from Bromleys Art Supplies. Price Match Promise. Fast Delivery. Great Customer Service.`;
            return {
                title: product.customFields?.seoTitle ?? preview.name,
                description,
                image: imagePreview ? `${imagePreview}?preset=large` : undefined,
                canonicalUrl: `/p/${preview.slug}`,
            };
        }),
    );

    ngOnInit() {
        const lastCollectionSlug$ = this.stateService.select((state) => state.lastCollectionSlug);
        this.productData$
            .pipe(filter(notNullOrUndefined), withLatestFrom(lastCollectionSlug$, this.productPreview$))
            .subscribe(([product, lastCollectionSlug, productPreview]) => {
                this.product = product;
                this.relatedBlogPostIds =
                    product.customFields?.relatedBlogTags
                        ?.map((t) => {
                            try {
                                return JSON.parse(t)?.id;
                            } catch (e) {
                                return;
                            }
                        })
                        .filter(notNullOrUndefined) ?? [];
                if (this.product.featuredAsset) {
                    this.selectedAssetId = this.product.featuredAsset.id;
                }
                const collection = getMostRelevantCollection(product.collections, lastCollectionSlug);
                this.breadcrumbs = collection
                    ? collection.breadcrumbs.concat({ name: productPreview.name, slug: '', id: '' })
                    : [];
                this.changeDetector.markForCheck();
                this.dotdigitalService.trackViewedProduct(product, productPreview);
            });

        this.jsonLdData$ = combineLatest([this.productData$, this.productPreview$]).pipe(
            map(([product, preview]) => this.jsonLdService.getProductData(product, preview)),
        );
    }

    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }

    addToCart(event: { productVariantId: string; quantity: number }) {
        this.orderService.addToCart(event).subscribe((result) => {
            if (result) {
                const variant = this.product?.variants.find((v) => v.id === event.productVariantId);
                if (variant) {
                    this.notificationService
                        .notify({
                            title: 'Added to cart',
                            type: 'info',
                            duration: 3000,
                            templateRef: this.addToCartTemplate,
                            templateContext: {
                                variant,
                                quantity: event.quantity,
                            },
                        })
                        .subscribe();
                }
            }
        });
    }

    updateOrderLine(event: { orderLineId: string; quantity: number }) {
        this.orderService.updateOrderLine(event).subscribe();
    }

    addToWishlist(data: { productVariantId: string; wishlistId?: string }) {
        this.orderService.addToWishlist(data).subscribe(() => {
            const variant = this.product?.variants.find((v) => v.id === data.productVariantId);
            if (variant) {
                this.notificationService
                    .notify({
                        title: 'Added to wishlist',
                        type: 'info',
                        duration: 3000,
                        templateRef: this.addToWishlistTemplate,
                        templateContext: {
                            variant,
                            wishlistId: data.wishlistId,
                        },
                    })
                    .subscribe();
            }
        });
    }

    viewCartFromNotification(closeFn: () => void) {
        this.stateService.setState('cartDrawerOpen', true);
        closeFn();
    }
}
